import React, { useState, useEffect } from 'react';
import axios from '../../http';
import { useParams, Link } from 'react-router-dom';

const TestResultsView = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchResults = async () => {
    try {
      const res = await axios.get(`/api/test-events/${id}/results`);
      setData(res.data);
    } catch (err) {
      console.error(err);
      setError('Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const res = await axios.get(`/api/test-events/${id}/export`, {
        responseType: 'blob'
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from header if possible, or default
      const contentDisposition = res.headers['content-disposition'];
      let fileName = `Results-${id}.xlsx`;
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch && fileNameMatch.length === 2) fileName = fileNameMatch[1];
      }

      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Failed to download report. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div style={{ padding: '2rem' }}>Loading results...</div>;
  if (error) return <div style={{ padding: '2rem', color: 'red' }}>{error}</div>;
  if (!data) return <div style={{ padding: '2rem' }}>No data found.</div>;

  const { event, stations, data: candidates } = data;

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}
      >
        <div>
          <h1 style={{ marginBottom: '0.5rem' }}>{event.name} - Results</h1>
          <p style={{ color: '#718096', margin: 0 }}>
            {new Date(event.event_date).toLocaleDateString()} | {event.location || 'No Location'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button
            onClick={handleDownload}
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            disabled={downloading}
          >
            <span>{downloading ? '⏳' : '📥'}</span>{' '}
            {downloading ? 'Generating...' : 'Download Excel'}
          </button>
          <Link to="/admin/history" className="btn btn-secondary">
            Back to History
          </Link>
        </div>
      </div>

      <div
        style={{
          overflowX: 'auto',
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
          <thead style={{ background: '#f7fafc', borderBottom: '2px solid #edf2f7' }}>
            <tr>
              <th
                style={{
                  padding: '1rem',
                  textAlign: 'left',
                  minWidth: '150px',
                  position: 'sticky',
                  left: 0,
                  background: '#f7fafc',
                  zIndex: 10
                }}
              >
                Candidate
              </th>
              {stations.map((st) => (
                <th key={st.id} style={{ padding: '1rem', textAlign: 'center', minWidth: '100px' }}>
                  <div>St {st.number}</div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 400, color: '#718096' }}>
                    {st.name}
                  </div>
                </th>
              ))}
              <th style={{ padding: '1rem', textAlign: 'center', minWidth: '120px' }}>
                Final Status
              </th>
            </tr>
          </thead>
          <tbody>
            {candidates.length === 0 ? (
              <tr>
                <td colSpan={stations.length + 2} style={{ padding: '2rem', textAlign: 'center' }}>
                  No candidates found.
                </td>
              </tr>
            ) : (
              candidates.map((c) => (
                <tr key={c.id} style={{ borderBottom: '1px solid #edf2f7' }}>
                  <td
                    style={{
                      padding: '1rem',
                      fontWeight: '500',
                      position: 'sticky',
                      left: 0,
                      background: 'white',
                      borderRight: '1px solid #edf2f7'
                    }}
                  >
                    {c.last_name}, {c.first_name}
                    <div style={{ fontSize: '0.8rem', color: '#718096' }}>ID: {c.candidate_id}</div>
                  </td>
                  {stations.map((st) => {
                    const result = c.results[st.id];
                    let bg = 'transparent';
                    let color = 'inherit';
                    if (result === 'pass') {
                      bg = '#f0fff4';
                      color = '#2f855a';
                    }
                    if (result === 'fail') {
                      bg = '#fff5f5';
                      color = '#c53030';
                    }

                    return (
                      <td
                        key={st.id}
                        style={{
                          padding: '1rem',
                          textAlign: 'center',
                          background: bg,
                          color: color,
                          fontWeight: '500'
                        }}
                      >
                        {result ? result.toUpperCase() : '-'}
                      </td>
                    );
                  })}
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    <span
                      style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontWeight: '600',
                        background:
                          c.final_status === 'pass'
                            ? '#c6f6d5'
                            : c.final_status === 'fail'
                              ? '#fed7d7'
                              : '#edf2f7',
                        color:
                          c.final_status === 'pass'
                            ? '#22543d'
                            : c.final_status === 'fail'
                              ? '#822727'
                              : '#4a5568'
                      }}
                    >
                      {c.final_status ? c.final_status.toUpperCase() : 'PENDING'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TestResultsView;
